import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { addInterface, editInterface, getSingleInterface } from "../../redux/services/interfaceSlice";
import { fileUpload } from "../../redux/services/fileUploadSlice";
import { ROUTES } from "../../constant";

const InterfaceForm = ({ action }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const singleInterface = useSelector((state) => state.interface.singleInterfaceData);

  const queryParams = new URLSearchParams(location.search);
  const interfaceId = queryParams.get("interfaceId");

  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const initialValues = {
    title: "",
    image: "",
    description: "",
    order: 0,
    isActive: true,
  };

  const validationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    image: Yup.string().required("Image is required"),
    description: Yup.string().required("Description is required"),
    order: Yup.number().required("Order is required"),
  });

  const onSubmit = async (values, { resetForm }) => {
    setLoading(true);
    try {
      if (interfaceId) values.interfaceId = interfaceId;
      let response = interfaceId
        ? await dispatch(editInterface(values))
        : await dispatch(addInterface(values));

      if (response?.payload?.status === 200) {
        toast.success(response?.payload?.message, {
          autoClose: 2000,
          pauseOnHover: false,
        });
        navigate(ROUTES.WEBSITE.INTERFACE);
        resetForm();
      }
    } catch (error) {
      console.error("Error saving interface:", error);
    } finally {
      setLoading(false);
    }
  };

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, setValues } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  const getSingleRecord = useCallback(async () => {
    try {
      await dispatch(getSingleInterface(interfaceId));
    } catch (error) {
      console.error("Error fetching single interface:", error);
    }
  }, [dispatch, interfaceId]);

  useEffect(() => {
    if (interfaceId) getSingleRecord();
  }, [interfaceId, getSingleRecord]);

  useEffect(() => {
    if (interfaceId && singleInterface) {
      setValues({
        title: singleInterface?.title ?? "",
        image: singleInterface?.image ?? "",
        description: singleInterface?.description ?? "",
        order: singleInterface?.order ?? 0,
        isActive: singleInterface?.isActive ?? true,
      });
      setPreview(singleInterface?.image ?? null);
    }
  }, [interfaceId, singleInterface, setValues]);

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const imageURL = URL.createObjectURL(file);
    setPreview(imageURL);
    const formData = new FormData();
    formData.append("image", file);
    try {
      setLoading(true);
      const response = await dispatch(fileUpload(formData));
      if (response) {
        const url = response.payload?.fileUrl || response.payload;
        setValues({ ...values, image: url });
        setLoading(false);
        toast.success("Image uploaded successfully!");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Image upload failed");
    }
  };

  return (
    <div className="bg-background py-4 sm:py-4">
      <div className="px-4 md:py-10 py-5 bg-card shadow rounded-3xl sm:p-10">
        <p className="md:text-3xl text-xl text-center font-medium uppercase text-primary">
          {action} Interface
        </p>

        <form className="mt-6" onSubmit={handleSubmit}>
          <div className="grid md:grid-cols-2 grid-cols-1 gap-5">
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-foreground">Title</label>
              <input
                type="text"
                name="title"
                value={values.title}
                onChange={handleChange}
                onBlur={handleBlur}
                className="w-full px-4 py-2 border border-border rounded-xl bg-card"
                placeholder="Enter title"
              />
              {touched.title && errors.title && <p className="text-red-500 text-sm">{errors.title}</p>}
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-medium text-foreground">Image</label>
              <input
                type="file"
                name="image"
                onChange={handleImageChange}
                className="w-full px-4 py-2 border border-border rounded-xl"
              />
              {touched.image && errors.image && <p className="text-red-500 text-sm">{errors.image}</p>}
              {preview && (
                <div className="mt-4 flex items-center gap-3">
                  <p className="text-md text-foreground">Preview:</p>
                  <img src={preview} alt="preview" className="w-20 h-20 object-cover border border-border rounded-xl" />
                </div>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-medium text-foreground">Description</label>
              <textarea
                name="description"
                rows={4}
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                className="w-full px-4 py-2 border border-border rounded-xl bg-card"
                placeholder="Enter description"
              />
              {touched.description && errors.description && (
                <p className="text-red-500 text-sm">{errors.description}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-medium text-foreground">Order</label>
              <input
                type="number"
                name="order"
                value={values.order}
                onChange={handleChange}
                onBlur={handleBlur}
                className="w-full px-4 py-2 border border-border rounded-xl bg-card"
                placeholder="Enter order"
              />
              {touched.order && errors.order && <p className="text-red-500 text-sm">{errors.order}</p>}
            </div>
          </div>

          <div className="flex justify-center">
            <button
              type="submit"
              className="w-full sm:w-[250px] min-h-[50px] flex justify-center items-center text-white bg-primary text-lg font-semibold rounded-xl hover:bg-accent mt-7"
            >
              {loading ? <div className="loader"></div> : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InterfaceForm;
